import {
  BulkUploadServiceGroupCsvHeadersEnum,
  CSV_VALIDATION_ERROR_CODE,
  SUPPORT_ERROR_MESSAGE,
} from '@/constants';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import * as state from '../state';
import * as analysisState from '@/components/Analysis/state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useBulkUploadServiceGroupsMutation } from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { GraphQLError } from 'graphql';
import { Menu, Typography, MenuItem, Button } from '@mui/material';
import { theme } from '@/theme/theme';
import { ServiceGroupUploadErrorDialog } from './ServiceGroupUploadErrorDialog';
import { ServiceGroupUploadErrorTableModal } from './ServiceGroupUploadErrorTableModal';
import { FileFixingUploadLoadingModal } from '../../shared';
import { downloadArrayAsXlsx } from '@/utils/xlsx';
import { GenericConfirmationDialog } from '@/components/Analysis/shared/GeneriConfirmationDialog';

type Props = {
  anchorEl: null | HTMLElement;
  setAnchorEl: (value: null | HTMLElement) => void;
};

export const ServiceGroupUploadMenu = ({ anchorEl, setAnchorEl }: Props) => {
  const setServiceGroupUploadState = useSetAtom(state.serviceGroupUploadState);
  const setApplyFilters = useSetAtom(analysisState.applyFilters);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingCsv, setPendingCsv] = useState<string | null>(null);
  const open = Boolean(anchorEl);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleClose = useCallback(() => {
    if (fileRef.current) fileRef.current.value = '';
    setAnchorEl(null);
  }, [setAnchorEl]);

  useEffect(() => {
    if (fileRef?.current) {
      setServiceGroupUploadState((previous) => ({ ...previous, inputRef: fileRef.current }));
    }
  }, [setServiceGroupUploadState]);

  const structureId = useGetStructureId();
  const [upload, { loading }] = useBulkUploadServiceGroupsMutation({
    refetchQueries: [],
    onCompleted: () => {
      // Still, a hack to refetch the EquipmentTable
      setApplyFilters(true);
    },
  });
  const { showSnackBar } = useSnackBarMessage({});

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setServiceGroupUploadState((previous) => ({ ...previous, state: 'idle' }));

    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const fileData = e.target?.result;
        if (!fileData) return;

        // Convert ArrayBuffer to string if needed
        const csvData =
          typeof fileData === 'string' ? fileData.replace(/^data:.*;base64,/, '') : '';

        // Pass the XLSX file data as a base64 string to the backend
        setPendingCsv(csvData);
        setConfirmOpen(true);
      } catch (error) {
        showSnackBar('Error processing file data. Please try again.', 'error');
        if (fileRef.current) fileRef.current.value = '';
      }
    };
    // Read as DataURL to get base64 string
    reader.readAsDataURL(file);
  };

  const onUploadData = async () => {
    if (!pendingCsv) return;
    setConfirmOpen(false);
    upload({
      variables: {
        input: {
          csv: pendingCsv,
          structureId,
        },
      },
    })
      .then(() => {
        showSnackBar(
          'File has been uploaded successfully. Please note that uploading this file will modify existing data. Review the file carefully before proceeding to ensure accuracy.',
          'success'
        );
      })
      .catch((error) => {
        if (fileRef.current) {
          fileRef.current.value = '';
        }
        const gqlError: GraphQLError | undefined = error?.graphQLErrors?.[0];

        if (!gqlError) {
          showSnackBar(error.message || SUPPORT_ERROR_MESSAGE, 'error');
          return;
        }
        if (gqlError.extensions?.code === CSV_VALIDATION_ERROR_CODE) {
          setServiceGroupUploadState((previous) => ({
            ...previous,
            state: 'csv-error',
            error: gqlError,
            inputCsv: pendingCsv,
          }));
          return;
        } else {
          setServiceGroupUploadState((previous) => ({
            ...previous,
            state: 'generic-error',
            error: gqlError,
            inputCsv: pendingCsv,
          }));
        }
      })
      // cleanup regardless of success/failure
      .finally(() => {
        setPendingCsv(null);
        if (fileRef.current) fileRef.current.value = '';
      });
  };
  const MENU_ITEMS: Array<{
    title: string;
    description: string;
    onClick?: () => void;
    disabled?: boolean;
  }> = useMemo(
    () => [
      {
        title: 'Upload new file',
        description: 'Values for the service group',
        onClick: () => {
          fileRef?.current?.click?.();
          handleClose();
        },
      },
      {
        title: 'Download template',
        description: 'Download template to view all the required columns.',
        onClick: () => {
          downloadArrayAsXlsx(
            Object.values(BulkUploadServiceGroupCsvHeadersEnum),
            'service-group-upload-template.xlsx'
          );
        },
      },
    ],
    [handleClose]
  );

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          mt: 2,
        }}
      >
        {MENU_ITEMS.map((item, index) => (
          <MenuItem
            disabled={item.disabled}
            key={index}
            onClick={item.onClick}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              my: 1,
            }}
          >
            <Typography color={theme.palette.primary.main}>{item.title}</Typography>
            <Typography color={theme.palette.primary.light}>{item.description}</Typography>
          </MenuItem>
        ))}
      </Menu>
      <input
        style={{ display: 'none' }}
        type="file"
        accept=".xlsx"
        onChange={onChange}
        ref={fileRef}
      />
      <GenericConfirmationDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          if (fileRef.current) fileRef.current.value = '';
          setPendingCsv(null);
        }}
        title="Confirm upload"
        description={
          'Please note that uploading this file will modify existing data. Review the file carefully before proceeding to ensure accuracy.'
        }
      >
        <Button
          onClick={() => {
            setConfirmOpen(false);
            onUploadData();
          }}
        >
          Confirm
        </Button>
        <Button
          onClick={() => {
            setConfirmOpen(false);
            if (fileRef.current) fileRef.current.value = '';
            setPendingCsv(null);
          }}
        >
          Cancel
        </Button>
      </GenericConfirmationDialog>
      <ServiceGroupUploadErrorDialog />
      <ServiceGroupUploadErrorTableModal />
      <FileFixingUploadLoadingModal open={loading} />
    </>
  );
};
